import { useCallback, useEffect, useMemo, useState } from 'react'
import { Copy, Plus, Scissors, Trash2 } from 'lucide-react'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { ServiceCategoriesSidebar } from '@/components/masters/ServiceCategoriesSidebar'
import { ServiceConsumablesPanel } from '@/components/masters/ServiceConsumablesPanel'
import { ServiceImageField } from '@/components/masters/ServiceImageField'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createServiceCategory,
  deleteServiceCategory,
  fetchServiceCategories,
  updateServiceCategory,
} from '@/lib/api/serviceCategories'
import {
  createService,
  deleteService,
  duplicateService,
  fetchServicesList,
  fetchServicesPage,
  SKILL_LEVEL_OPTIONS,
  updateService,
} from '@/lib/api/services'
import { getServiceCommissionAmount, getServiceDisplayPrice, SALON_DESK_MASTERS } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

function emptyCategoryForm() {
  return {
    name: '',
    gender_scope: 'ladies',
    sort: 0,
    colour: '#A84D62',
    bookable_online_default: true,
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').ServiceCategory} category
 */
function categoryToForm(category) {
  return {
    name: category.name ?? '',
    gender_scope: category.gender_scope ?? 'ladies',
    sort: category.sort ?? 0,
    colour: category.colour ?? '#A84D62',
    bookable_online_default: Boolean(category.bookable_online_default),
    is_active: Boolean(category.is_active),
  }
}

/**
 * @param {number | string | null | undefined} value
 */
function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * @param {import('@/types/api').ServiceCategory | null} category
 */
function emptyForm(category) {
  return {
    service_category_id: category?.id ?? '',
    name: '',
    name_ar: '',
    duration: 30,
    price_ladies: '',
    price_gents: '',
    commission_percent: '',
    skill_level: 'standard',
    bookable_online: Boolean(category?.bookable_online_default ?? true),
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').Service} service
 */
function serviceToForm(service) {
  return {
    service_category_id: service.service_category_id ?? '',
    name: service.name ?? '',
    name_ar: service.name_ar ?? '',
    duration: service.duration ?? 30,
    price_ladies: toNumber(service.price_ladies),
    price_gents: toNumber(service.price_gents),
    commission_percent:
      service.commission_percent === null || service.commission_percent === undefined
        ? ''
        : toNumber(service.commission_percent),
    skill_level: service.skill_level ?? 'standard',
    bookable_online: service.bookable_online !== false,
    is_active: Boolean(service.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function formatPrice(value) {
  const amount = toNumber(value)
  return amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  {
    key: 'category',
    label: 'Category',
    getValue: (row) => row.category?.name ?? '',
  },
  { key: 'duration', label: 'Duration (min)', getValue: (row) => row.duration },
  ...(SALON_DESK_MASTERS.singleServicePrice
    ? [{ key: 'price', label: 'Price', getValue: (row) => getServiceDisplayPrice(row) }]
    : [
        { key: 'price_ladies', label: 'Price ladies', getValue: (row) => toNumber(row.price_ladies) },
        { key: 'price_gents', label: 'Price gents', getValue: (row) => toNumber(row.price_gents) },
      ]),
  ...(SALON_DESK_MASTERS.showServiceCommission
    ? [
        {
          key: 'commission_percent',
          label: 'Commission %',
          getValue: (row) => toNumber(row.commission_percent),
        },
      ]
    : []),
  ...(SALON_DESK_MASTERS.showServiceArabicName
    ? [{ key: 'name_ar', label: 'Arabic name', getValue: (row) => row.name_ar ?? '' }]
    : []),
  {
    key: 'bookable_online',
    label: 'Online store',
    getValue: (row) => (row.bookable_online !== false ? 'yes' : 'no'),
  },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function ServicesPage() {
  const { activeBranchId } = useBranch()
  const [categories, setCategories] = useState([])
  const [filterCategoryId, setFilterCategoryId] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedService, setSelectedService] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm(null))
  const [reloadKey, setReloadKey] = useState(0)
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [categoryCreating, setCategoryCreating] = useState(false)
  const [categorySelectedId, setCategorySelectedId] = useState(null)
  const [categorySelected, setCategorySelected] = useState(null)
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm())
  const [categorySubmitting, setCategorySubmitting] = useState(false)
  const [categoryDeleting, setCategoryDeleting] = useState(false)
  const [categoryError, setCategoryError] = useState('')

  const fetchPage = useCallback((params) => fetchServicesPage(params), [])

  const loadCategories = useCallback(async () => {
    const items = await fetchServiceCategories()
    setCategories(items)
    return items
  }, [])

  const extraParams = useMemo(() => {
    if (!filterCategoryId) return {}
    return { category_id: Number(filterCategoryId) }
  }, [filterCategoryId])

  const previewCommissionAmount = useMemo(() => {
    const price = SALON_DESK_MASTERS.singleServicePrice
      ? toNumber(form.price_ladies)
      : toNumber(form.price_ladies)
    return getServiceCommissionAmount(price, form.commission_percent)
  }, [form.price_ladies, form.commission_percent])

  useEffect(() => {
    let active = true
    setSuccess('')
    setError('')

    async function load() {
      try {
        const items = await loadCategories()
        if (!active) {
          return
        }

        if (SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage && items.length > 0) {
          setFilterCategoryId(String(items[0].id))
        } else {
          setFilterCategoryId('')
        }
      } catch (err) {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load categories.')
        }
      }
    }

    load()
    return () => {
      active = false
    }
  }, [activeBranchId, loadCategories])

  function openCreate() {
    const category =
      categories.find((item) => String(item.id) === filterCategoryId) ?? categories[0] ?? null
    setCreating(true)
    setSelectedId(null)
    setSelectedService(null)
    setForm(emptyForm(category))
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').Service} service
   */
  function openEdit(service) {
    setCreating(false)
    setSelectedId(service.id)
    setSelectedService(service)
    setForm(serviceToForm(service))
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting || deleting) return
    setModalOpen(false)
    setError('')
    setSuccess('')
  }

  async function handleDuplicate() {
    if (!selectedId || creating) {
      return
    }

    setDuplicating(true)
    setError('')
    setSuccess('')

    try {
      const copy = await duplicateService(selectedId)
      setReloadKey((current) => current + 1)
      openEdit(copy)
      setSuccess('Service duplicated.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to duplicate service.')
    } finally {
      setDuplicating(false)
    }
  }

  async function handleDelete() {
    if (!selectedId || creating) {
      return
    }

    const serviceName = form.name.trim() || 'this service'
    if (
      !window.confirm(
        `Delete "${serviceName}"? This cannot be undone. The service will be removed from POS and booking menus. Past sales are kept but will no longer link to this service. Services linked to appointments or packages cannot be deleted — mark inactive instead.`,
      )
    ) {
      return
    }

    setDeleting(true)
    setError('')
    setSuccess('')

    try {
      await deleteService(selectedId)
      setReloadKey((current) => current + 1)
      setSuccess('Service deleted.')
      setModalOpen(false)
    } catch (err) {
      setError(
        getFieldError(err, 'service') ??
          err?.response?.data?.message ??
          'Unable to delete service.',
      )
    } finally {
      setDeleting(false)
    }
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  function openCategoryCreate() {
    setCategoryCreating(true)
    setCategorySelectedId(null)
    setCategorySelected(null)
    setCategoryForm(emptyCategoryForm())
    setCategoryError('')
    setCategoryModalOpen(true)
  }

  /**
   * @param {import('@/types/api').ServiceCategory} category
   */
  function openCategoryEdit(category) {
    setCategoryCreating(false)
    setCategorySelectedId(category.id)
    setCategorySelected(category)
    setCategoryForm(categoryToForm(category))
    setCategoryError('')
    setCategoryModalOpen(true)
  }

  function closeCategoryModal() {
    if (categorySubmitting || categoryDeleting) {
      return
    }
    setCategoryModalOpen(false)
    setCategoryError('')
  }

  function updateCategoryField(field, value) {
    setCategoryForm((current) => ({ ...current, [field]: value }))
  }

  async function handleCategoryDelete() {
    if (!categorySelectedId || categoryCreating) {
      return
    }

    const categoryName = categoryForm.name.trim() || 'this category'
    const servicesCount = categorySelected?.services_count ?? 0
    const servicesNote =
      servicesCount > 0
        ? ` This category still has ${servicesCount} service(s) — delete or move them first.`
        : ''

    if (
      !window.confirm(
        `Delete "${categoryName}"? This cannot be undone.${servicesNote}`,
      )
    ) {
      return
    }

    setCategoryDeleting(true)
    setCategoryError('')

    try {
      await deleteServiceCategory(categorySelectedId)
      const items = await loadCategories()
      if (items.length > 0) {
        setFilterCategoryId(String(items[0].id))
      } else {
        setFilterCategoryId('')
      }
      setReloadKey((current) => current + 1)
      setSuccess('Category deleted.')
      setCategoryModalOpen(false)
    } catch (err) {
      setCategoryError(
        getFieldError(err, 'category') ??
          err?.response?.data?.message ??
          'Unable to delete category.',
      )
    } finally {
      setCategoryDeleting(false)
    }
  }

  async function handleCategorySubmit(event) {
    event.preventDefault()
    setCategorySubmitting(true)
    setCategoryError('')

    const payload = {
      ...categoryForm,
      sort: Number(categoryForm.sort) || 0,
      colour: categoryForm.colour || null,
    }

    try {
      if (categoryCreating) {
        const created = await createServiceCategory(payload)
        await loadCategories()
        setFilterCategoryId(String(created.id))
        setSuccess('Category created.')
      } else if (categorySelectedId) {
        await updateServiceCategory(categorySelectedId, payload)
        await loadCategories()
        setSuccess('Category saved.')
      }
      setCategoryModalOpen(false)
    } catch (err) {
      setCategoryError(
        getFieldError(err, 'name') ??
          err?.response?.data?.message ??
          'Unable to save category.',
      )
    } finally {
      setCategorySubmitting(false)
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const singlePrice = toNumber(form.price_ladies)
    const payload = {
      ...form,
      service_category_id: Number(form.service_category_id),
      duration: Number(form.duration) || 30,
      price_ladies: SALON_DESK_MASTERS.singleServicePrice ? singlePrice : toNumber(form.price_ladies),
      price_gents: SALON_DESK_MASTERS.singleServicePrice ? singlePrice : toNumber(form.price_gents),
      commission_percent:
        form.commission_percent === '' || form.commission_percent === null
          ? null
          : toNumber(form.commission_percent),
      name_ar: SALON_DESK_MASTERS.showServiceArabicName ? form.name_ar || null : null,
    }

    try {
      if (creating) {
        await createService(payload)
        setSuccess('Service created.')
      } else if (selectedId) {
        await updateService(selectedId, payload)
        setSuccess('Service saved.')
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'service_category_id') ??
        getFieldError(err, 'duration') ??
        getFieldError(err, 'price_ladies') ??
        getFieldError(err, 'price_gents') ??
        getFieldError(err, 'commission_percent') ??
        err?.response?.data?.message ??
        'Unable to save service.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const hasCategories = categories.length > 0

  return (
    <div className="salon-page py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Scissors className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Services</h1>
            <p className="text-sm text-muted-foreground">
              {SALON_DESK_MASTERS.lite
                ? 'Your price list — categories on the left, services on the right.'
                : 'Menu items with dual pricing, duration, Arabic name, and online booking.'}
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate} disabled={!hasCategories}>
          <Plus className="h-4 w-4" />
          New service
        </Button>
      </div>

      {success && !modalOpen ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      {error && !modalOpen ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {!hasCategories ? (
        <div className="card-surface p-6 text-sm text-muted-foreground">
          {SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>Create a category first, then add services.</span>
              <Button type="button" variant="outline" onClick={openCategoryCreate}>
                <Plus className="h-4 w-4" />
                New category
              </Button>
            </div>
          ) : (
            'Create at least one service category before adding services.'
          )}
        </div>
      ) : (
        <div
          className={cn(
            'grid gap-5',
            SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage
              ? 'lg:grid-cols-[240px_minmax(0,1fr)]'
              : '',
          )}
        >
          {SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage ? (
            <ServiceCategoriesSidebar
              categories={categories}
              selectedCategoryId={filterCategoryId}
              onSelectCategory={setFilterCategoryId}
              onAddCategory={openCategoryCreate}
              onEditCategory={openCategoryEdit}
            />
          ) : null}

          <MasterServerTable
            key={activeBranchId}
            fetchPage={fetchPage}
            reloadKey={reloadKey}
            extraParams={extraParams}
            getRowKey={(service) => service.id}
            onEdit={openEdit}
            emptyLabel="No services in this category yet."
            emptyActionLabel={SALON_DESK_MASTERS.lite ? 'Add service' : undefined}
            onEmptyAction={SALON_DESK_MASTERS.lite ? openCreate : undefined}
            searchPlaceholder="Search services…"
            exportColumns={EXPORT_COLUMNS}
            exportFilename="services.csv"
            fetchAllForExport={(params) => fetchServicesList(params)}
            toolbar={
              !SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage ? (
                <select
                  value={filterCategoryId}
                  onChange={(event) => setFilterCategoryId(event.target.value)}
                  aria-label="Filter by category"
                  className={cn(
                    'h-10 min-w-[180px] rounded-xl border border-border/80 bg-card px-3 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              ) : null
            }
            columns={[
              { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
              ...(!SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage
                ? [
                    {
                      key: 'category',
                      label: 'Category',
                      render: (row) => row.category?.name ?? '—',
                    },
                  ]
                : []),
              {
                key: 'duration_minutes',
                label: 'Duration',
                sortable: true,
                render: (row) => `${row.duration} min`,
              },
              ...(SALON_DESK_MASTERS.singleServicePrice
                ? [
                    {
                      key: 'price',
                      label: 'Price',
                      render: (row) => formatPrice(getServiceDisplayPrice(row)),
                    },
                  ]
                : [
                    {
                      key: 'price_ladies',
                      label: 'Ladies',
                      render: (row) => formatPrice(row.price_ladies),
                    },
                    {
                      key: 'price_gents',
                      label: 'Gents',
                      render: (row) => formatPrice(row.price_gents),
                    },
                  ]),
              ...(SALON_DESK_MASTERS.showServiceCommission
                ? [
                    {
                      key: 'commission',
                      label: 'Commission',
                      render: (row) => {
                        const percent = toNumber(row.commission_percent)
                        if (percent <= 0) return '—'
                        const amount = getServiceCommissionAmount(getServiceDisplayPrice(row), percent)
                        return `${percent}% (${formatPrice(amount)})`
                      },
                    },
                  ]
                : []),
              {
                key: 'bookable_online',
                label: 'Online store',
                render: (row) => (row.bookable_online !== false ? 'Yes' : 'No'),
              },
              {
                key: 'is_active',
                label: 'Status',
                render: (row) => (row.is_active ? 'Active' : 'Inactive'),
              },
            ]}
          />
        </div>
      )}

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={creating ? 'New service' : 'Edit service'}
        description={
          SALON_DESK_MASTERS.lite
            ? 'Name, duration, price, staff commission %, and online booking.'
            : 'Menu item with dual pricing, duration, Arabic name, and online booking.'
        }
        size={SALON_DESK_MASTERS.lite ? 'lg' : '2xl'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {SALON_DESK_MASTERS.showServiceImage ? (
            <ServiceImageField
              service={selectedService}
              disabled={submitting}
              onUpdated={(updated) => {
                setSelectedService(updated)
                setReloadKey((current) => current + 1)
                setSuccess('Service photo updated.')
              }}
            />
          ) : null}

          {!SALON_DESK_MASTERS.mergeCategoriesIntoServicesPage ? (
            <div>
              <label htmlFor="service-category" className="mb-1.5 block text-sm font-medium">
                Category
              </label>
              <select
                id="service-category"
                value={form.service_category_id}
                onChange={(e) => updateField('service_category_id', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              >
                <option value="" disabled>
                  Select category
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="rounded-xl bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              Category:{' '}
              <span className="font-medium text-foreground">
                {categories.find((item) => String(item.id) === String(form.service_category_id))
                  ?.name ?? '—'}
              </span>
            </p>
          )}

          <div>
            <label htmlFor="service-name" className="mb-1.5 block text-sm font-medium">
              Name
            </label>
            <input
              id="service-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              required
            />
          </div>

          {SALON_DESK_MASTERS.showServiceArabicName ? (
            <div>
              <label htmlFor="service-name-ar" className="mb-1.5 block text-sm font-medium">
                Name (Arabic)
              </label>
              <input
                id="service-name-ar"
                type="text"
                dir="rtl"
                value={form.name_ar}
                onChange={(e) => updateField('name_ar', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          ) : null}

          <div
            className={cn(
              'grid gap-4',
              SALON_DESK_MASTERS.singleServicePrice
                ? SALON_DESK_MASTERS.showServiceCommission
                  ? 'sm:grid-cols-3'
                  : 'sm:grid-cols-2'
                : 'md:grid-cols-4',
            )}
          >
            <div>
              <label htmlFor="service-duration" className="mb-1.5 block text-sm font-medium">
                Duration (min)
              </label>
              <input
                id="service-duration"
                type="number"
                min={5}
                max={1440}
                value={form.duration}
                onChange={(e) => updateField('duration', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="service-price-ladies" className="mb-1.5 block text-sm font-medium">
                {SALON_DESK_MASTERS.singleServicePrice ? 'Price' : 'Price — ladies'}
              </label>
              <input
                id="service-price-ladies"
                type="number"
                min={0}
                step="0.01"
                value={form.price_ladies}
                onChange={(e) => updateField('price_ladies', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            {SALON_DESK_MASTERS.showServiceCommission ? (
              <div>
                <label htmlFor="service-commission" className="mb-1.5 block text-sm font-medium">
                  Staff commission (%)
                </label>
                <input
                  id="service-commission"
                  type="number"
                  min={0}
                  max={100}
                  step="0.5"
                  value={form.commission_percent}
                  onChange={(e) => updateField('commission_percent', e.target.value)}
                  placeholder="e.g. 30"
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
                {previewCommissionAmount > 0 ? (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Staff earns{' '}
                    <span className="font-medium text-foreground">
                      AED {formatPrice(previewCommissionAmount)}
                    </span>{' '}
                    per service at this price
                  </p>
                ) : null}
              </div>
            ) : null}
            {SALON_DESK_MASTERS.showDualPricing ? (
              <div>
                <label htmlFor="service-price-gents" className="mb-1.5 block text-sm font-medium">
                  Price — gents
                </label>
                <input
                  id="service-price-gents"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price_gents}
                  onChange={(e) => updateField('price_gents', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
            ) : null}
            {SALON_DESK_MASTERS.showServiceSkillLevel ? (
              <div>
                <label htmlFor="service-skill" className="mb-1.5 block text-sm font-medium">
                  Skill level
                </label>
                <select
                  id="service-skill"
                  value={form.skill_level}
                  onChange={(e) => updateField('skill_level', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  {SKILL_LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.bookable_online}
              onChange={(e) => updateField('bookable_online', e.target.checked)}
              className="rounded border-border"
            />
            Show on online store
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Service is active
          </label>

          {!creating && selectedId && SALON_DESK_MASTERS.showServiceConsumables ? (
            <ServiceConsumablesPanel serviceId={selectedId} disabled={submitting} />
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            {!creating && selectedId ? (
              <div className="flex flex-wrap gap-2">
                {SALON_DESK_MASTERS.showServiceDuplicate ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDuplicate}
                    disabled={duplicating || submitting || deleting}
                  >
                    <Copy className="h-4 w-4" />
                    {duplicating ? 'Duplicating…' : 'Duplicate'}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting || submitting || duplicating}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Deleting…' : 'Delete service'}
                </Button>
              </div>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={closeModal} disabled={submitting || deleting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || deleting}>
                {submitting ? 'Saving…' : creating ? 'Create service' : 'Save service'}
              </Button>
            </div>
          </div>
        </form>
      </MasterFormModal>

      <MasterFormModal
        open={categoryModalOpen}
        onClose={closeCategoryModal}
        title={categoryCreating ? 'New category' : 'Edit category'}
        description="Group services into menu sections."
      >
        <form onSubmit={handleCategorySubmit} className="space-y-4">
          <div>
            <label htmlFor="category-name" className="mb-1.5 block text-sm font-medium">
              Name
            </label>
            <input
              id="category-name"
              type="text"
              value={categoryForm.name}
              onChange={(e) => updateCategoryField('name', e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              required
            />
          </div>

          {!SALON_DESK_MASTERS.showCategoryGenderScope ? null : (
            <div>
              <label htmlFor="category-gender" className="mb-1.5 block text-sm font-medium">
                Gender scope
              </label>
              <select
                id="category-gender"
                value={categoryForm.gender_scope}
                onChange={(e) => updateCategoryField('gender_scope', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                <option value="ladies">Ladies</option>
                <option value="unisex">Unisex</option>
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={categoryForm.is_active}
              onChange={(e) => updateCategoryField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Category is active
          </label>

          {categoryError ? (
            <p className="text-sm text-destructive" role="alert">
              {categoryError}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            {!categoryCreating && categorySelectedId ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleCategoryDelete}
                disabled={categoryDeleting || categorySubmitting}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                {categoryDeleting ? 'Deleting…' : 'Delete category'}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeCategoryModal}
                disabled={categorySubmitting || categoryDeleting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={categorySubmitting || categoryDeleting}>
                {categorySubmitting
                  ? 'Saving…'
                  : categoryCreating
                    ? 'Create category'
                    : 'Save category'}
              </Button>
            </div>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
